/**
 * Fetching a generated PDF and either opening it or handing it to the phone's
 * native share sheet (WhatsApp, Gmail, Drive, Bluetooth — whatever the device
 * offers).
 *
 * This is Web Share **Level 2** — sharing an actual file rather than a link.
 * A link would be useless here: the API needs an Authorization header the
 * customer's phone will never have, so anything shared has to be the bytes
 * themselves. Level 2 is Android Chrome and iOS Safari; desktop browsers
 * mostly implement only Level 1 (title/text/url) and are handled by falling
 * back to opening the PDF in a tab, which is what the buttons did before.
 *
 * The awkward part is **transient user activation**. `navigator.share()` may
 * only be called shortly after a real tap — Chrome allows about five seconds
 * — and we have to fetch (and possibly render) the PDF in between. That
 * usually fits now, but a slow counter connection can overrun it, and the
 * browser then rejects the share with NotAllowedError. Rather than show a
 * failure, that case parks the already-downloaded file and asks for one more
 * tap, which shares instantly because there is nothing left to fetch.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** Level 2 support, tested against the real file — `canShare` is per-file, not a static capability. */
function canShareFile(file) {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.share === 'function' &&
    typeof navigator.canShare === 'function' &&
    navigator.canShare({ files: [file] })
  );
}

/**
 * The server names the file in Content-Disposition, and that name is what the
 * recipient sees in WhatsApp — worth getting right rather than sending
 * everyone "document.pdf".
 *
 * The two forms are decoded differently, which is easy to get wrong: RFC 6266
 * `filename="…"` is a plain quoted string and must be used verbatim, while
 * `filename*=UTF-8''…` is percent-encoded and has to be decoded. Running
 * decodeURIComponent over the plain form throws URIError on any invoice
 * number containing a bare `%` — a stray percent in a numbering prefix would
 * otherwise have broken sharing outright.
 */
export function fileNameFrom(header, fallback) {
  const text = header ?? '';
  // The extended form wins when both are present — that is what it is for.
  const extended = /filename\*=\s*[^']*''([^;]+)/i.exec(text);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      return extended[1].trim(); // malformed escape — better the raw name than nothing
    }
  }
  const plain = /filename=\s*"?([^";]+)"?/i.exec(text);
  return plain ? plain[1].trim() : fallback;
}

function openBlobInTab(blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  // Revoked on a timer rather than immediately: the new tab needs the URL to
  // stay alive long enough to load it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * @param {() => Promise<Response>} fetchPdf - authenticated request returning the PDF response
 * @param {object} [meta]
 * @param {string} [meta.fallbackName] - used if the server sends no filename
 * @param {string} [meta.title] - share sheet title
 * @param {string} [meta.text] - prefilled message body in chat apps
 */
export function useSharePdf(fetchPdf, meta = {}) {
  const { fallbackName = 'document.pdf', title, text } = meta;

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Set when the file is downloaded but the tap that started it has gone
  // stale — the UI offers one more tap and this shares without refetching.
  const [awaitingTap, setAwaitingTap] = useState(false);
  const readyFile = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const shareFile = useCallback(
    async (file) => {
      try {
        await navigator.share({ files: [file], ...(title ? { title } : {}), ...(text ? { text } : {}) });
        readyFile.current = null;
        if (mounted.current) setAwaitingTap(false);
        return true;
      } catch (err) {
        // The share sheet opened and the user backed out of it. Not a failure,
        // and showing an error for it would be actively wrong.
        if (err?.name === 'AbortError') {
          readyFile.current = null;
          if (mounted.current) setAwaitingTap(false);
          return true;
        }
        // The tap that started this expired while the PDF was being fetched.
        // The file is in hand — ask for one more tap rather than refetching.
        if (err?.name === 'NotAllowedError') {
          readyFile.current = file;
          if (mounted.current) setAwaitingTap(true);
          return false;
        }
        throw err;
      }
    },
    [title, text],
  );

  const share = useCallback(async () => {
    // Second tap of the stale-activation path: nothing to fetch.
    if (readyFile.current) {
      const file = readyFile.current;
      try {
        await shareFile(file);
      } catch {
        if (mounted.current) {
          setError('Could not open the share sheet. Use Print / View to open the PDF instead.');
          setAwaitingTap(false);
        }
        readyFile.current = null;
      }
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const response = await fetchPdf();
      const blob = await response.blob();
      const name = fileNameFrom(response.headers.get('Content-Disposition'), fallbackName);
      const file = new File([blob], name, { type: blob.type || 'application/pdf' });

      if (!canShareFile(file)) {
        // Desktop, or a browser without Level 2 — do the thing the button
        // used to do rather than dead-ending.
        openBlobInTab(blob);
        return;
      }

      await shareFile(file);
    } catch (err) {
      if (mounted.current) setError(err?.message ?? 'Could not prepare the PDF to share.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [fetchPdf, fallbackName, shareFile]);

  const open = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetchPdf();
      openBlobInTab(await response.blob());
    } catch (err) {
      if (mounted.current) setError(err?.message ?? 'Could not open the PDF.');
    } finally {
      if (mounted.current) setBusy(false);
    }
  }, [fetchPdf]);

  const dismissError = useCallback(() => setError(null), []);

  return { share, open, busy, error, awaitingTap, dismissError };
}

export default useSharePdf;
